// networking/ws.handlers.ts — WebSocket message handlers
// Network System: Translates network messages into state changes and effects.
// This file contains the pure "reducer" part of the architecture.

import type { LobbyBrowserMessage } from "@game/domain";
import type { WebSocketMessage } from "@game/protocol";
import { MAX_HEALTH } from "@game/config";
import { gameActions, gameStateStore } from "@game/state";
import { createWorld, pushEffect, type WorldDeps } from "../ecs/core";
import { applyEffects } from "../ecs/effects";
import { triggerMineExplosion } from "../effects/presets";
import { createLogger } from "../logger";
import {
  DEFAULT_ANGULAR_VELOCITY,
  DEFAULT_DEATHS,
  DEFAULT_KILLS,
  DEFAULT_MULTIPLIER,
  DEFAULT_PELLETS_COLLECTED,
  DEFAULT_ROTATION,
  DEFAULT_SCALE,
  DEFAULT_TIMESTAMP,
  DEFAULT_VELOCITY,
  TOAST_DURATION_EXTRA_LONG,
  TOAST_DURATION_LONG,
  TOAST_DURATION_NORMAL,
  TOAST_DURATION_SHORT,
  TOAST_DURATION_VERY_LONG,
  TWO_PI,
  VISIBILITY_MARGIN_DEFAULT,
} from "./ws.constants";

const logger = createLogger("ws-handlers");

export interface HandlerDependencies extends WorldDeps {}
let deps!: HandlerDependencies;
let world: ReturnType<typeof createWorld>;

export function setHandlerDependencies(dependencies: HandlerDependencies) {
  deps = dependencies;
  world = createWorld(deps);
}

function upsertPlayer(state: any, p: any) {
  const current = state.players.get(p.id);
  const base = {
    id: p.id,
    name: p.name,
    color: p.color,
    pos: { x: p.pos.x, y: p.pos.y },
    vel: { x: p.vel?.x || DEFAULT_VELOCITY, y: p.vel?.y || DEFAULT_VELOCITY },
    rotation: p.rotation || DEFAULT_ROTATION,
    targetRotation: p.targetRotation ?? p.rotation ?? DEFAULT_ROTATION,
    angularVelocity: p.angularVelocity || DEFAULT_ANGULAR_VELOCITY,
    health: p.health,
    maxHealth: p.maxHealth,
    kills: p.kills,
    deaths: p.deaths,
    pelletsCollected: p.pelletsCollected || DEFAULT_PELLETS_COLLECTED,
    scale: p.scale || DEFAULT_SCALE,
    damageMultiplier: p.damageMultiplier || DEFAULT_MULTIPLIER,
    speedMultiplier: p.speedMultiplier || DEFAULT_MULTIPLIER,
    lastMineDeployAt: p.lastMineDeployAt || DEFAULT_TIMESTAMP,
    lastBoostAt: p.lastBoostAt || DEFAULT_TIMESTAMP,
    teamId: p.teamId,
  };

  if (!current) {
    state.players.set(p.id, base);
  } else {
    Object.assign(current, {
      name: base.name,
      color: base.color,
      vel: base.vel,
      rotation: base.rotation,
      targetRotation: base.targetRotation,
      angularVelocity: base.angularVelocity,
      health: base.health,
      maxHealth: base.maxHealth,
      kills: base.kills,
      deaths: base.deaths,
      pelletsCollected: base.pelletsCollected,
      scale: base.scale,
      damageMultiplier: base.damageMultiplier,
      speedMultiplier: base.speedMultiplier,
      lastMineDeployAt: base.lastMineDeployAt,
      lastBoostAt: base.lastBoostAt,
      teamId: base.teamId,
    });
  }

  world.comps.playersTarget.set(p.id, base);
}

function upsertAsteroid(state: any, a: any) {
  const existing = state.asteroids.get(a.id);
  const asteroidData = {
    id: a.id,
    pos: { x: a.pos.x, y: a.pos.y },
    vel: { x: a.vel.x, y: a.vel.y },
    sizeLevel: a.sizeLevel,
    rotation: ((a.rotation % TWO_PI) + TWO_PI) % TWO_PI,
    rotationSpeed: a.rotationSpeed,
    health: a.health,
    maxHealth: a.maxHealth,
  };
  if (!existing) {
    state.asteroids.set(a.id, asteroidData);
  }
  state.asteroidsTarget.set(a.id, asteroidData);
}

function upsertPellet(state: any, p: any) {
  if (!state.pellets.has(p.id)) {
    state.pellets.set(p.id, {
      id: p.id,
      pos: { x: p.pos.x, y: p.pos.y },
      vel: { x: p.vel?.x || DEFAULT_VELOCITY, y: p.vel?.y || DEFAULT_VELOCITY },
      age: p.age,
      value: p.value,
      lifetime: p.lifetime,
    });
  }
  state.pelletsTarget.set(p.id, {
    id: p.id,
    pos: { x: p.pos.x, y: p.pos.y },
    vel: { x: p.vel?.x || DEFAULT_VELOCITY, y: p.vel?.y || DEFAULT_VELOCITY },
    age: p.age,
    value: p.value,
    lifetime: p.lifetime,
  });
}

function applyMobaStateToStore(payload: {
  bases: Array<{
    id: string;
    teamId: number;
    pos: { x: number; y: number };
    health: number;
    maxHealth: number;
    radius: number;
  }>;
  npcs: Array<{
    id: string;
    teamId: number;
    laneId: string;
    pos: { x: number; y: number };
    vel: { x: number; y: number };
    health: number;
    maxHealth: number;
    radius: number;
  }>;
  waveNumber: number;
}): void {
  const basesMap = new Map(payload.bases.map((base) => [base.id, base]));
  const npcsMap = new Map(payload.npcs.map((npc) => [npc.id, npc]));
  gameStateStore.setState((current) => ({
    ...current,
    mobaBases: basesMap,
    mobaNpcs: npcsMap,
    mobaWaveNumber: payload.waveNumber,
  }));
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Large switch statement for message routing
// biome-ignore lint/complexity/noExcessiveLinesPerFunction: Centralized message handler with all game logic
export function applyMessage(m: WebSocketMessage) {
  const s = deps.state;

  switch (m.t) {
    case "hello": {
      s.playerId = m.playerId;
      pushEffect(world, {
        kind: "storage.set",
        key: "playerId",
        value: m.playerId,
      });
      world.comps.lobbyPeakStats.clear();
      logger.debug(`[WS] Stored playerId: ${m.playerId}`);
      if (m.world) s.world = m.world;

      gameActions.setGlobalState({ playerId: m.playerId });
      break;
    }

    case "error": {
      if (
        m.message.includes("Invalid party code") ||
        m.message.includes("Party not found")
      ) {
        pushEffect(world, { kind: "storage.remove", key: "partyJoinCode" });
        const savedName = deps.getPlayerName();
        pushEffect(world, {
          kind: "ws.send",
          payload: { t: "party_create", playerName: savedName },
        });
        pushEffect(world, {
          kind: "toast",
          message: "Squadron no longer exists, created new one",
          level: "warning",
        });
      } else {
        pushEffect(world, {
          kind: "toast",
          message: `Error: ${m.message}`,
          level: "error",
        });
      }
      break;
    }

    case "joined": {
      s.lobbyId = m.lobbyId;
      pushEffect(world, {
        kind: "toast",
        message: `Joined lobby ${m.lobbyId}`,
        level: "success",
      });
      pushEffect(world, { kind: "ui.updateLobbyDisplay", lobbyId: m.lobbyId });
      pushEffect(world, { kind: "ui.buttons", inLobby: true });
      pushEffect(world, { kind: "ui.topbar", show: true });
      pushEffect(world, { kind: "ui.leaderboard.visible", show: true });
      pushEffect(world, { kind: "ui.resizeCanvas" });

      s.parallaxCamera.x = s.camera.x;
      s.parallaxCamera.y = s.camera.y;

      pushEffect(world, { kind: "ui.joinModal", show: false });
      pushEffect(world, {
        kind: "ui.joinButtonsReset",
        enabled: true,
        label: "JOIN BATTLE",
      });

      gameActions.setGlobalState({
        lobbyId: m.lobbyId,
        gameStarted: false,
      });
      gameActions.resetGameState();
      break;
    }

    case "left": {
      s.lobbyId = null;
      s.started = false;
      s.players.clear();
      s.asteroids.clear();
      s.asteroidsTarget.clear();
      s.pellets.clear();
      s.pelletsTarget.clear();
      world.comps.lobbyPeakStats.clear();
      pushEffect(world, {
        kind: "toast",
        message: "Left lobby",
        level: "info",
      });
      pushEffect(world, { kind: "ui.updateLobbyDisplay", lobbyId: null });
      pushEffect(world, { kind: "ui.buttons", inLobby: false });
      pushEffect(world, { kind: "ui.gameStats", show: false });
      pushEffect(world, { kind: "ui.perfStats", show: false });
      pushEffect(world, { kind: "ui.topbar", show: false });
      pushEffect(world, { kind: "ui.leaderboard.visible", show: false });
      pushEffect(world, { kind: "ui.resizeCanvas" });
      pushEffect(world, { kind: "ui.render" });
      pushEffect(world, { kind: "ui.joinModal", show: true });
      pushEffect(world, { kind: "lobby.requestList" });

      gameActions.setGlobalState({
        lobbyId: null,
        gameStarted: false,
      });
      gameActions.resetGameState();
      break;
    }

    case "lobby_state": {
      const wasStarted = s.started;
      s.lobbyId = m.lobbyId;
      s.started = m.started;
      s.lobbyPlayers = m.players;

      logger.info("[LOBBY_STATE] Received teamMode:", m.teamMode);

      pushEffect(world, { kind: "ui.leaderboard.visible", show: true });

      if (m.started && !wasStarted) {
        pushEffect(world, {
          kind: "toast",
          message: "Game started! Good luck!",
          level: "success",
          ms: TOAST_DURATION_LONG,
        });
      }

      if (m.secondsUntilStart && m.secondsUntilStart > 0 && !m.started) {
        pushEffect(world, {
          kind: "ui.countdown",
          active: true,
          seconds: m.secondsUntilStart,
          text: "Game Starting",
        });
      } else {
        pushEffect(world, { kind: "ui.countdown", active: false });
      }

      pushEffect(world, {
        kind: "ui.playersPills",
        names: m.players.map((p: any) => ({
          id: p.id,
          name: p.name,
          self: p.id === s.playerId,
        })),
      });

      gameActions.setGlobalState({
        lobbyId: m.lobbyId,
        gameStarted: m.started,
        lobbyTeamMode:
          (m.teamMode as "ffa" | "teams" | "ctf" | "custom" | undefined) ??
          null,
      });

      const lobbyPlayersAsStorePlayers = m.players.map((p) => ({
        id: p.id,
        name: p.name,
        color: p.color,
        pos: { x: 0, y: 0 },
        vel: { x: 0, y: 0 },
        rotation: 0,
        targetRotation: 0,
        angularVelocity: 0,
        health: MAX_HEALTH,
        maxHealth: MAX_HEALTH,
        kills: 0,
        deaths: 0,
        pelletsCollected: 0,
        totalKills: 0,
        totalDeaths: 0,
        totalPelletsCollected: 0,
        captures: 0,
        scale: 1.0,
        damageMultiplier: 1.0,
        speedMultiplier: 1.0,
        lastMineDeployAt: 0,
        lastBoostAt: 0,
        lastAcknowledgedSequence: 0,
        primaryWeapon: "DEFAULT",
      }));

      gameActions.applyFullState({
        players: lobbyPlayersAsStorePlayers,
        bullets: [],
        asteroids: [],
        pellets: [],
        mines: [],
        timestamp: Date.now(),
      });
      break;
    }

    case "game_state": {
      s.started = true;
      s.lastStateAt = deps.now();

      if (
        deps.gameStatsEl &&
        (!deps.gameStatsEl.style.display ||
          deps.gameStatsEl.style.display === "none")
      ) {
        pushEffect(world, { kind: "ui.gameStats", show: true });
      }

      if (m.performance) {
        s.performance = m.performance;
        pushEffect(world, { kind: "ui.perfStats", show: true });
        const latency = s.lastPingTime ? Date.now() - s.lastPingTime : 0;
        pushEffect(world, {
          kind: "metrics.smoothed",
          tps: m.performance.tps,
          frameTime: m.performance.frameTime,
          entities: m.performance.entities,
          ping: latency,
        });
      }

      for (const p of m.players) {
        upsertPlayer(s, p);
        if (p.id === s.playerId) {
          const currentPelletsCollected =
            p.pelletsCollected || DEFAULT_PELLETS_COLLECTED;
          if (currentPelletsCollected > deps.previousPelletsCollected.value) {
            const pelletValueCollected =
              currentPelletsCollected - deps.previousPelletsCollected.value;
            pushEffect(world, {
              kind: "sound",
              sound: "pickup",
              amount: pelletValueCollected,
            });
          }
          deps.previousPelletsCollected.value = currentPelletsCollected;

          const serverPlayer = {
            pos: { x: p.pos.x, y: p.pos.y },
            vel: {
              x: p.vel?.x || DEFAULT_VELOCITY,
              y: p.vel?.y || DEFAULT_VELOCITY,
            },
            rotation: p.rotation || DEFAULT_ROTATION,
            targetRotation: p.targetRotation ?? p.rotation ?? DEFAULT_ROTATION,
            angularVelocity: p.angularVelocity || DEFAULT_ANGULAR_VELOCITY,
            health: p.health,
            maxHealth: p.maxHealth,
            kills: p.kills,
            deaths: p.deaths,
            name: p.name,
            color: p.color,
            pelletsCollected: p.pelletsCollected || DEFAULT_PELLETS_COLLECTED,
            totalKills: p.totalKills || 0,
            totalDeaths: p.totalDeaths || 0,
            totalPelletsCollected: p.totalPelletsCollected || 0,
            captures: p.captures || 0,
            scale: p.scale || DEFAULT_SCALE,
            damageMultiplier: p.damageMultiplier || DEFAULT_MULTIPLIER,
            speedMultiplier: p.speedMultiplier || DEFAULT_MULTIPLIER,
            lastMineDeployAt: p.lastMineDeployAt || DEFAULT_TIMESTAMP,
            lastBoostAt: p.lastBoostAt || DEFAULT_TIMESTAMP,
          };
          const acknowledgedSeq =
            p.lastAcknowledgedSequence || s.lastAcknowledgedSequence;
          deps.reconcileWithServer(serverPlayer, acknowledgedSeq);

          const lobbyId = s.lobbyId as string | undefined;
          if (lobbyId) {
            const existing = world.comps.lobbyPeakStats.get(lobbyId) || {
              kills: DEFAULT_KILLS,
              deaths: DEFAULT_DEATHS,
              pelletsCollected: DEFAULT_PELLETS_COLLECTED,
              maxHealth: MAX_HEALTH,
              damageMultiplier: DEFAULT_MULTIPLIER,
              speedMultiplier: DEFAULT_MULTIPLIER,
            };
            world.comps.lobbyPeakStats.set(lobbyId, {
              kills: Math.max(existing.kills, p.kills || DEFAULT_KILLS),
              deaths: Math.max(existing.deaths, p.deaths || DEFAULT_DEATHS),
              pelletsCollected: Math.max(
                existing.pelletsCollected,
                p.pelletsCollected || DEFAULT_PELLETS_COLLECTED,
              ),
              maxHealth: Math.max(
                existing.maxHealth,
                p.maxHealth || MAX_HEALTH,
              ),
              damageMultiplier: Math.max(
                existing.damageMultiplier,
                p.damageMultiplier || DEFAULT_MULTIPLIER,
              ),
              speedMultiplier: Math.max(
                existing.speedMultiplier,
                p.speedMultiplier || DEFAULT_MULTIPLIER,
              ),
            });
          }
        }
      }

      for (const [id] of s.players) {
        if (!m.players.find((p: any) => p.id === id)) {
          s.players.delete(id);
          s.playersTarget.delete(id);
        }
      }

      // MUTATE IN-PLACE to preserve reference
      s.bullets.length = 0;
      if (m.bullets) s.bullets.push(...m.bullets);
      pushEffect(world, { kind: "bullets.recompute" });

      s.mines.length = 0;
      if (m.mines) s.mines.push(...m.mines);

      const serverAsteroids = m.asteroids || [];
      for (const a of serverAsteroids) upsertAsteroid(s, a);
      for (const [id] of s.asteroids) {
        if (!serverAsteroids.find((x: any) => x.id === id)) {
          s.asteroids.delete(id);
          s.asteroidsTarget.delete(id);
        }
      }

      const serverPellets = m.pellets || [];
      for (const p of serverPellets) upsertPellet(s, p);
      for (const [id] of s.pellets) {
        if (!serverPellets.find((x: any) => x.id === id)) {
          s.pellets.delete(id);
          s.pelletsTarget.delete(id);
        }
      }

      gameActions.setGlobalState({ gameStarted: true });

      // Update flags in client.ts state (for canvas rendering)
      if (m.gameModeState?.flags) {
        s.flags.length = 0;
        s.flags.push(...m.gameModeState.flags);
      }

      gameActions.applyFullState({
        players: m.players,
        bullets: m.bullets || [],
        asteroids: m.asteroids || [],
        pellets: m.pellets || [],
        mines: m.mines || [],
        flags: m.gameModeState?.flags || [],
        timestamp: deps.now(),
      });
      break;
    }

    case "game_delta": {
      if (!s.lastSequence) s.lastSequence = 0;

      if (m.sequence !== undefined) {
        if (m.sequence <= s.lastSequence) {
          logger.warn(
            `[DELTA] Out of order sequence: received ${m.sequence}, expected > ${s.lastSequence}`,
          );
          return;
        }
        const expected = s.lastSequence + 1;
        if (m.sequence > expected) {
          logger.warn(
            `[DELTA] Sequence gap detected: received ${m.sequence}, expected ${expected} (gap: ${m.sequence - expected})`,
          );
        }
      }

      s.started = true;
      s.lastStateAt = deps.now();
      s.lastSequence = m.sequence;

      if (
        deps.gameStatsEl &&
        (!deps.gameStatsEl.style.display ||
          deps.gameStatsEl.style.display === "none")
      ) {
        pushEffect(world, { kind: "ui.gameStats", show: true });
      }

      if (m.performance) {
        s.performance = m.performance;
        pushEffect(world, { kind: "ui.perfStats", show: true });
        const latency = s.lastPingTime ? Date.now() - s.lastPingTime : 0;
        pushEffect(world, {
          kind: "metrics.smoothed",
          tps: m.performance.tps,
          frameTime: m.performance.frameTime,
          entities: m.performance.entities,
          ping: latency,
        });
      }

      for (const id of m.deletedPlayerIds || []) {
        s.players.delete(id);
        s.playersTarget.delete(id);
      }
      for (const p of [
        ...(m.createdPlayers || []),
        ...(m.updatedPlayers || []),
      ]) {
        upsertPlayer(s, p);
        if (p.id === s.playerId) {
          const currentPelletsCollected =
            p.pelletsCollected || DEFAULT_PELLETS_COLLECTED;
          if (currentPelletsCollected > deps.previousPelletsCollected.value) {
            const pelletValueCollected =
              currentPelletsCollected - deps.previousPelletsCollected.value;
            pushEffect(world, {
              kind: "sound",
              sound: "pickup",
              amount: pelletValueCollected,
            });
          }
          deps.previousPelletsCollected.value = currentPelletsCollected;

          const serverPlayer = {
            pos: { x: p.pos.x, y: p.pos.y },
            vel: {
              x: p.vel?.x || DEFAULT_VELOCITY,
              y: p.vel?.y || DEFAULT_VELOCITY,
            },
            rotation: p.rotation || DEFAULT_ROTATION,
            targetRotation: p.targetRotation ?? p.rotation ?? DEFAULT_ROTATION,
            angularVelocity: p.angularVelocity || DEFAULT_ANGULAR_VELOCITY,
            health: p.health,
            maxHealth: p.maxHealth,
            kills: p.kills,
            deaths: p.deaths,
            name: p.name,
            color: p.color,
            pelletsCollected: p.pelletsCollected || DEFAULT_PELLETS_COLLECTED,
            totalKills: p.totalKills || 0,
            totalDeaths: p.totalDeaths || 0,
            totalPelletsCollected: p.totalPelletsCollected || 0,
            captures: p.captures || 0,
            scale: p.scale || DEFAULT_SCALE,
            damageMultiplier: p.damageMultiplier || DEFAULT_MULTIPLIER,
            speedMultiplier: p.speedMultiplier || DEFAULT_MULTIPLIER,
            lastMineDeployAt: p.lastMineDeployAt || DEFAULT_TIMESTAMP,
            lastBoostAt: p.lastBoostAt || DEFAULT_TIMESTAMP,
          };
          const acknowledgedSeq =
            p.lastAcknowledgedSequence || s.lastAcknowledgedSequence;
          deps.reconcileWithServer(serverPlayer, acknowledgedSeq);
        }
      }

      // MUTATE IN-PLACE to preserve reference
      const deleteBulletIds = new Set(m.deletedBulletIds || []);
      if (s.bullets?.length) {
        let w = 0;
        for (let r = 0; r < s.bullets.length; r++) {
          const item = s.bullets[r];
          if (!deleteBulletIds.has(item.id)) s.bullets[w++] = item;
        }
        s.bullets.length = w;
      }
      for (const b of m.createdBullets || []) s.bullets.push(b);
      for (const b of m.updatedBullets || []) {
        const idx = s.bullets.findIndex((x: any) => x.id === b.id);
        if (idx !== -1) s.bullets[idx] = b;
      }
      pushEffect(world, { kind: "bullets.recompute" });

      for (const id of m.deletedAsteroidIds || []) {
        s.asteroids.delete(id);
        s.asteroidsTarget.delete(id);
      }
      for (const a of [
        ...(m.createdAsteroids || []),
        ...(m.updatedAsteroids || []),
      ]) {
        upsertAsteroid(s, a);
      }

      for (const id of m.deletedPelletIds || []) {
        s.pellets.delete(id);
        s.pelletsTarget.delete(id);
      }
      for (const p of [
        ...(m.createdPellets || []),
        ...(m.updatedPellets || []),
      ]) {
        upsertPellet(s, p);
      }

      // MUTATE IN-PLACE to preserve reference
      const deleteMineIds = new Set(m.deletedMineIds || []);
      if (s.mines?.length) {
        let w = 0;
        for (let r = 0; r < s.mines.length; r++) {
          const m0 = s.mines[r];
          if (!deleteMineIds.has(m0.id)) s.mines[w++] = m0;
        }
        s.mines.length = w;
      }
      for (const mine of m.createdMines || []) {
        const idx = s.mines.findIndex((x: any) => x.id === mine.id);
        if (idx === -1) s.mines.push(mine);
        else s.mines[idx] = mine;
      }
      for (const mine of m.updatedMines || []) {
        const idx = s.mines.findIndex((x: any) => x.id === mine.id);
        if (idx !== -1) s.mines[idx] = mine;
      }

      // Update flags in client.ts state (for canvas rendering)
      if (m.gameModeState?.flags) {
        s.flags.length = 0;
        s.flags.push(...m.gameModeState.flags);
      }

      logger.trace("[DELTA] Applied delta update", {
        sequence: m.sequence,
        players:
          (m.createdPlayers?.length || 0) + (m.updatedPlayers?.length || 0),
        bullets:
          (m.createdBullets?.length || 0) + (m.updatedBullets?.length || 0),
      });

      gameActions.setGlobalState({ gameStarted: true });

      gameActions.applyDeltaState({
        timestamp: deps.now(),
        createdPlayers: m.createdPlayers,
        updatedPlayers: m.updatedPlayers,
        deletedPlayerIds: m.deletedPlayerIds,
        createdBullets: m.createdBullets,
        updatedBullets: m.updatedBullets,
        deletedBulletIds: m.deletedBulletIds,
        createdAsteroids: m.createdAsteroids,
        updatedAsteroids: m.updatedAsteroids,
        deletedAsteroidIds: m.deletedAsteroidIds,
        createdPellets: m.createdPellets,
        updatedPellets: m.updatedPellets,
        deletedPelletIds: m.deletedPelletIds,
        createdMines: m.createdMines,
        updatedMines: m.updatedMines,
        deletedMineIds: m.deletedMineIds,
        flags: m.gameModeState?.flags,
      });
      break;
    }

    case "player_killed": {
      if (m.killerId === s.playerId) {
        pushEffect(world, {
          kind: "toast",
          message: `You killed ${m.victimName}!`,
          level: "success",
          ms: TOAST_DURATION_LONG,
        });
        pushEffect(world, {
          kind: "screen.flash",
          intensity: 0.4,
          r: 1,
          g: 1,
          b: 1,
        });
      } else if (m.victimId === s.playerId) {
        pushEffect(world, {
          kind: "toast",
          message: `You were killed by ${m.killerName}`,
          level: "error",
          ms: TOAST_DURATION_LONG,
        });
        pushEffect(world, {
          kind: "screen.flash",
          intensity: 0.6,
          r: 1,
          g: 0,
          b: 0,
        });
        pushEffect(world, { kind: "screen.shake", intensity: 15 });
      } else {
        pushEffect(world, {
          kind: "toast",
          message: `${m.killerName} killed ${m.victimName}`,
          level: "info",
          ms: TOAST_DURATION_NORMAL,
        });
      }

      if (m.deathX !== undefined && m.deathY !== undefined) {
        pushEffect(world, {
          kind: "webgl.gpuExplosion",
          x: m.deathX,
          y: m.deathY,
          intensity: 1.0,
        });
      } else {
        const victim = s.players.get(m.victimId);
        if (victim) {
          pushEffect(world, {
            kind: "webgl.gpuExplosion",
            x: victim.pos.x,
            y: victim.pos.y,
            intensity: 1.0,
          });
        }
      }
      break;
    }

    case "player_crashed": {
      if (m.playerId === s.playerId) {
        pushEffect(world, {
          kind: "toast",
          message: `You crashed at ${m.speed} px/s!`,
          level: "error",
          ms: TOAST_DURATION_LONG,
        });
        pushEffect(world, {
          kind: "screen.flash",
          intensity: 0.6,
          r: 1,
          g: 0,
          b: 0,
        });
        pushEffect(world, { kind: "screen.shake", intensity: 15 });
      } else {
        pushEffect(world, {
          kind: "toast",
          message: `${m.playerName} crashed at ${m.speed} px/s`,
          level: "info",
          ms: TOAST_DURATION_NORMAL,
        });
      }

      const crashedPlayer = s.players.get(m.playerId);
      if (crashedPlayer) {
        pushEffect(world, {
          kind: "webgl.gpuExplosion",
          x: crashedPlayer.pos.x,
          y: crashedPlayer.pos.y,
          intensity: 1.0,
        });
      }
      break;
    }

    case "player_left": {
      pushEffect(world, {
        kind: "toast",
        message: `${m.playerName} left the game`,
        level: "info",
        ms: TOAST_DURATION_NORMAL,
      });
      break;
    }

    case "player_joined": {
      pushEffect(world, {
        kind: "toast",
        message: `${m.playerName} joined the game`,
        level: "success",
        ms: TOAST_DURATION_NORMAL,
      });
      break;
    }

    case "eliminated": {
      pushEffect(world, {
        kind: "ui.deathModal",
        show: true,
        title: "ELIMINATED!",
        titleClass: "",
        message: m.reason || "You have been eliminated from the battle!",
      });

      const lobbyId = s.lobbyId as string | undefined;
      const peakStats = lobbyId
        ? world.comps.lobbyPeakStats.get(lobbyId)
        : null;
      if (peakStats) {
        pushEffect(world, { kind: "ui.deathStats", stats: peakStats });
      }

      pushEffect(world, {
        kind: "toast",
        message: "You have been eliminated!",
        level: "error",
        ms: TOAST_DURATION_VERY_LONG,
      });
      break;
    }

    case "game_over": {
      if (m.winnerId === s.playerId) {
        const myPlayer = s.playerId ? s.players.get(s.playerId) : null;
        pushEffect(world, {
          kind: "ui.deathModal",
          show: true,
          title: "VICTORY!",
          titleClass: "victory",
          message: "You are the last survivor!",
        });

        if (myPlayer) {
          pushEffect(world, {
            kind: "ui.deathStats",
            stats: {
              kills: myPlayer.kills || DEFAULT_KILLS,
              deaths: myPlayer.deaths || DEFAULT_DEATHS,
              pelletsCollected:
                myPlayer.pelletsCollected || DEFAULT_PELLETS_COLLECTED,
              maxHealth: myPlayer.maxHealth || MAX_HEALTH,
              damageMultiplier: myPlayer.damageMultiplier || DEFAULT_MULTIPLIER,
              speedMultiplier: myPlayer.speedMultiplier || DEFAULT_MULTIPLIER,
            },
          });
        }

        pushEffect(world, {
          kind: "toast",
          message: "VICTORY! You are the champion!",
          level: "success",
          ms: TOAST_DURATION_EXTRA_LONG,
        });
      } else {
        pushEffect(world, {
          kind: "toast",
          message: `${m.winnerName} won the game!`,
          level: "info",
          ms: TOAST_DURATION_VERY_LONG,
        });
      }
      break;
    }

    case "game_end": {
      const localPlayer = s.playerId ? s.players.get(s.playerId) : null;
      const localTeamId = localPlayer?.teamId;

      const isWinner =
        !m.isDraw && localTeamId !== undefined && localTeamId === m.winner;

      pushEffect(world, {
        kind: "ui.showGameEnd",
        isWinner,
        isDraw: m.isDraw,
        reason: m.reason,
        finalScores: m.finalScores,
        playerStats: m.playerStats,
        localTeamId,
        localPlayerId: s.playerId,
      });
      break;
    }

    case "player_eliminated": {
      if (m.playerId !== s.playerId) {
        pushEffect(world, {
          kind: "toast",
          message: `${m.playerName} was eliminated! ${m.remainingPlayers} players remaining`,
          level: "info",
          ms: TOAST_DURATION_NORMAL,
        });
      }
      break;
    }

    case "player_crashed_asteroid": {
      if (m.playerId === s.playerId) {
        pushEffect(world, {
          kind: "toast",
          message: "You crashed into an asteroid!",
          level: "error",
          ms: TOAST_DURATION_LONG,
        });
      } else {
        pushEffect(world, {
          kind: "toast",
          message: `${m.playerName} crashed into an asteroid`,
          level: "info",
          ms: TOAST_DURATION_NORMAL,
        });
      }

      const crashedPlayer = s.players.get(m.playerId);
      if (crashedPlayer) {
        pushEffect(world, {
          kind: "webgl.gpuExplosion",
          x: crashedPlayer.pos.x,
          y: crashedPlayer.pos.y,
          intensity: 1.0,
        });
      }
      break;
    }

    case "player_collision_death": {
      if (m.playerId === s.playerId) {
        if (m.killerName) {
          pushEffect(world, {
            kind: "toast",
            message: `You collided with ${m.killerName} and died!`,
            level: "error",
            ms: TOAST_DURATION_LONG,
          });
        } else {
          pushEffect(world, {
            kind: "toast",
            message: "You died from a collision!",
            level: "error",
            ms: TOAST_DURATION_LONG,
          });
        }
        pushEffect(world, {
          kind: "screen.flash",
          intensity: 0.6,
          r: 1,
          g: 0,
          b: 0,
        });
        pushEffect(world, { kind: "screen.shake", intensity: 15 });
      } else if (m.killerId === s.playerId) {
        pushEffect(world, {
          kind: "toast",
          message: `${m.playerName} died in collision with you!`,
          level: "success",
          ms: TOAST_DURATION_LONG,
        });
      } else {
        if (m.killerName) {
          pushEffect(world, {
            kind: "toast",
            message: `${m.playerName} collided with ${m.killerName} and died!`,
            level: "info",
            ms: TOAST_DURATION_NORMAL,
          });
        } else {
          pushEffect(world, {
            kind: "toast",
            message: `${m.playerName} died from a collision!`,
            level: "info",
            ms: TOAST_DURATION_NORMAL,
          });
        }
      }

      const crashedPlayer = s.players.get(m.playerId);
      if (crashedPlayer) {
        pushEffect(world, {
          kind: "webgl.gpuExplosion",
          x: crashedPlayer.pos.x,
          y: crashedPlayer.pos.y,
          intensity: 1.0,
        });
      }
      break;
    }

    case "player_mine_death": {
      if (m.playerId === s.playerId) {
        if (m.killerName) {
          pushEffect(world, {
            kind: "toast",
            message: `You were killed by ${m.killerName}'s mine!`,
            level: "error",
            ms: TOAST_DURATION_LONG,
          });
        } else {
          pushEffect(world, {
            kind: "toast",
            message: "You died from a mine explosion!",
            level: "error",
            ms: TOAST_DURATION_LONG,
          });
        }
        pushEffect(world, {
          kind: "screen.flash",
          intensity: 0.6,
          r: 1,
          g: 0,
          b: 0,
        });
        pushEffect(world, { kind: "screen.shake", intensity: 15 });
      } else if (m.killerId === s.playerId) {
        pushEffect(world, {
          kind: "toast",
          message: `Your mine killed ${m.playerName}!`,
          level: "success",
          ms: TOAST_DURATION_LONG,
        });
      } else {
        if (m.killerName) {
          pushEffect(world, {
            kind: "toast",
            message: `${m.playerName} was killed by ${m.killerName}'s mine!`,
            level: "info",
            ms: TOAST_DURATION_NORMAL,
          });
        } else {
          pushEffect(world, {
            kind: "toast",
            message: `${m.playerName} died from a mine explosion!`,
            level: "info",
            ms: TOAST_DURATION_NORMAL,
          });
        }
      }
      break;
    }

    case "player_count": {
      break;
    }

    case "damage_dealt": {
      // Only show damage numbers for damage that happens in visible areas
      if (
        deps.CoordinateValidator.validateMessage(m) &&
        deps.isVisible(m.x, m.y, VISIBILITY_MARGIN_DEFAULT)
      ) {
        pushEffect(world, {
          kind: "ui.damageNumber",
          x: m.x,
          y: m.y,
          damage: m.damage,
          source: m.source,
        });

        if (m.source === "asteroid") {
          pushEffect(world, {
            kind: "webgl.gpuExplosion",
            x: m.x,
            y: m.y,
            intensity: 0.8,
          });
        }
      }

      const localPlayer = s.playerId ? s.players.get(s.playerId) : null;
      if (localPlayer && localPlayer.health > 0) {
        const dx = Math.abs(m.x - localPlayer.pos.x);
        const dy = Math.abs(m.y - localPlayer.pos.y);
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 50) {
          const damageIntensity = Math.min(m.damage / 100, 0.5);
          pushEffect(world, {
            kind: "screen.flash",
            intensity: damageIntensity,
            r: 1,
            g: 0,
            b: 0,
          });
        }
      }
      break;
    }

    case "damage_batch": {
      // Process batched damage events (MP-3 optimization)
      const localPlayer = s.playerId ? s.players.get(s.playerId) : null;

      for (const event of m.events) {
        // Only show damage numbers for damage that happens in visible areas
        if (deps.isVisible(event.x, event.y, VISIBILITY_MARGIN_DEFAULT)) {
          pushEffect(world, {
            kind: "ui.damageNumber",
            x: event.x,
            y: event.y,
            damage: event.damage,
            source: event.source,
          });

          if (event.source === "asteroid") {
            pushEffect(world, {
              kind: "webgl.gpuExplosion",
              x: event.x,
              y: event.y,
              intensity: 0.8,
            });
          }
        }

        if (localPlayer && localPlayer.health > 0) {
          const dx = Math.abs(event.x - localPlayer.pos.x);
          const dy = Math.abs(event.y - localPlayer.pos.y);
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 50) {
            const damageIntensity = Math.min(event.damage / 100, 0.5);
            pushEffect(world, {
              kind: "screen.flash",
              intensity: damageIntensity,
              r: 1,
              g: 0,
              b: 0,
            });
          }
        }
      }
      break;
    }

    case "asteroid_destroyed": {
      pushEffect(world, { kind: "sound", sound: "impact" });
      if (deps.CoordinateValidator.validateMessage(m)) {
        pushEffect(world, {
          kind: "webgl.gpuExplosion",
          x: m.x,
          y: m.y,
          intensity: 1.0,
        });
        pushEffect(world, { kind: "screen.shake", intensity: 3 });
      }
      break;
    }

    case "mine_exploded": {
      triggerMineExplosion(world, m.x, m.y, m.radius);
      break;
    }

    case "flags_update": {
      s.flags.length = 0;
      s.flags.push(...m.flags);

      s.gameModeState = {
        mode: "ctf",
        teamScores: m.teamScores,
        captureLimit: m.captureLimit,
        flags: m.flags.map((flag: any) => ({
          id: flag.id,
          teamId: flag.teamId,
          state: flag.state,
          carrierId: flag.carrierId || undefined,
        })),
      };

      gameActions.applyDeltaState({
        timestamp: deps.now(),
        flags: m.flags,
      });

      gameActions.setGlobalState({
        gameModeState: s.gameModeState,
      });

      pushEffect(world, {
        kind: "ui.setGameModeState",
        gameModeState: s.gameModeState,
      });

      logger.debug("[FLAGS_UPDATE] Received flags", {
        count: m.flags.length,
        teamScores: m.teamScores,
      });
      break;
    }

    case "flag_taken": {
      const teamName = m.teamId === 0 ? "Blue" : "Red";
      pushEffect(world, {
        kind: "toast",
        message: `${m.playerName} took the ${teamName} flag!`,
        level: "warning",
      });
      break;
    }

    case "flag_dropped": {
      const teamName = m.teamId === 0 ? "Blue" : "Red";
      pushEffect(world, {
        kind: "toast",
        message: `${teamName} flag dropped!`,
        level: "info",
      });
      break;
    }

    case "flag_returned": {
      const teamName = m.teamId === 0 ? "Blue" : "Red";
      pushEffect(world, {
        kind: "toast",
        message: `${teamName} flag returned by ${m.playerName}`,
        level: "success",
      });
      pushEffect(world, {
        kind: "screen.shake",
        intensity: 8,
      });
      break;
    }

    case "flag_captured": {
      const capturingTeam = m.capturingTeamId === 0 ? "Blue" : "Red";
      const flagTeam = m.flagTeamId === 0 ? "Blue" : "Red";
      pushEffect(world, {
        kind: "toast",
        message: `${capturingTeam} team scores! ${m.playerName} captured the ${flagTeam} flag (${m.newScore})`,
        level: "success",
      });
      break;
    }

    case "moba_state": {
      s.gameModeState = {
        mode: "moba",
        teamScores: m.teamScores,
        flags: [],
      };
      applyMobaStateToStore({
        bases: m.bases,
        npcs: m.npcs,
        waveNumber: m.waveNumber,
      });
      pushEffect(world, {
        kind: "ui.setGameModeState",
        gameModeState: s.gameModeState,
      });
      break;
    }

    case "base_damaged": {
      const teamName = m.baseTeamId === 0 ? "Blue" : "Red";
      pushEffect(world, {
        kind: "toast",
        message: `${teamName} base took ${Math.round(m.damage)} damage (${Math.max(0, Math.round(m.newHealth))} HP)`,
        level: "warning",
      });
      break;
    }

    case "base_destroyed": {
      const teamName = m.baseTeamId === 0 ? "Blue" : "Red";
      pushEffect(world, {
        kind: "toast",
        message: `${teamName} base destroyed!`,
        level: "error",
      });
      pushEffect(world, {
        kind: "screen.shake",
        intensity: 12,
      });
      break;
    }

    case "npc_killed": {
      if (m.killerId && m.killerId === s.playerId) {
        pushEffect(world, {
          kind: "toast",
          message: `+${m.xpAwarded} XP (NPC eliminated)`,
          level: "success",
          ms: TOAST_DURATION_SHORT,
        });
      }
      break;
    }

    case "wave_spawned": {
      pushEffect(world, {
        kind: "toast",
        message: `Wave ${m.waveNumber} deployed (${m.npcsSpawned} NPCs)`,
        level: "info",
      });
      break;
    }

    case "mine_deployed": {
      if (m.mine) {
        s.mines.push(m.mine);
      }
      break;
    }

    case "pong": {
      break;
    }

    case "lobby_list": {
      try {
        const lobbyMessage = m as LobbyBrowserMessage & { t: "lobby_list" };
        if (!lobbyMessage.lobbies) {
          pushEffect(world, {
            kind: "toast",
            message: "Invalid lobby list received",
            level: "error",
          });
        } else {
          if (lobbyMessage.lobbies?.length > 0) {
            logger.debug(
              "🎯 CLIENT RECEIVED LOBBY:",
              lobbyMessage.lobbies[0]?.id,
              "teamMode:",
              lobbyMessage.lobbies[0]?.teamMode,
            );
          }
          pushEffect(world, {
            kind: "lobby.updateList",
            lobbies: lobbyMessage.lobbies,
          });
        }
      } catch (_error) {
        pushEffect(world, {
          kind: "toast",
          message: "Failed to update lobby list",
          level: "error",
        });
      }
      break;
    }

    case "party_state": {
      logger.debug("[PARTY_STATE] Received update:", m.party);
      logger.debug(
        "[PARTY_STATE] Members:",
        m.party?.members.map((member: any) => `${member.name} (${member.id})`),
      );

      s.party = m.party;

      if (m.party?.joinCode) {
        pushEffect(world, {
          kind: "storage.set",
          key: "partyJoinCode",
          value: m.party.joinCode,
        });
      } else {
        pushEffect(world, { kind: "storage.remove", key: "partyJoinCode" });
      }

      logger.debug(
        "[PARTY_STATE] Updated state.party, pushing react.updateParty effect",
      );
      pushEffect(world, { kind: "react.updateParty", party: m.party });

      if (m.party) {
        if (m.party.members.length > 1) {
          const otherMembers = m.party.members.filter(
            (member: any) => member.id !== s.playerId,
          );
          if (otherMembers.length > 0) {
            pushEffect(world, {
              kind: "toast",
              message: `Party updated: ${m.party.members.length} member(s)`,
              level: "success",
              ms: TOAST_DURATION_SHORT,
            });
          }
        }
      } else {
        pushEffect(world, {
          kind: "toast",
          message: "Left party",
          level: "info",
          ms: TOAST_DURATION_SHORT,
        });
      }

      gameActions.setGlobalState({ party: m.party });
      break;
    }

    default: {
      logger.warn(`[WS] Unhandled message type: ${(m as any).t}`);
      break;
    }
  }
}

export const handleGameMessage = (m: WebSocketMessage) => {
  applyMessage(m);
  applyEffects(world);
};

export const registerAllMessageHandlers = (
  registerMessageHandler: (type: string, handler: any) => void,
) => {
  const types: WebSocketMessage["t"][] = [
    "hello",
    "error",
    "joined",
    "left",
    "lobby_state",
    "game_state",
    "game_delta",
    "player_killed",
    "player_crashed",
    "player_left",
    "player_joined",
    "eliminated",
    "game_over",
    "game_end",
    "player_eliminated",
    "player_crashed_asteroid",
    "player_collision_death",
    "player_mine_death",
    "player_count",
    "damage_dealt",
    "damage_batch",
    "asteroid_destroyed",
    "mine_exploded",
    "mine_deployed",
    "flags_update",
    "flag_taken",
    "flag_dropped",
    "flag_returned",
    "flag_captured",
    "moba_state",
    "base_damaged",
    "base_destroyed",
    "npc_killed",
    "wave_spawned",
    "pong",
    "lobby_list",
    "party_state",
  ];
  for (const t of types) registerMessageHandler(t, handleGameMessage);
};
